import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { DatePicker, Flex, Button } from 'antd';
import dayjs from 'dayjs';
import BaseButton from 'components/atoms/baseButton';
import { DateFormats, PresetKeyEnum } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  CalendarOutlined,
  DoubleRightOutlined,
  DoubleLeftOutlined,
  RightOutlined,
  LeftOutlined,
} from '@ant-design/icons';

import {
  createRangePresets,
  MIN_RANGE_DAYS,
  MAX_RANGE_MONTHS,
} from './constants';
import styles from './styles.module.scss';

import type { ITimeframePreset } from './types';
import type { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

interface TimeframePickerProps {
  selectedRange: [Dayjs | null, Dayjs | null];
  onRangeChange: (
    dates: null | (Dayjs | null)[],
    dateStrings: string[],
    presetKey?: string | null,
  ) => void;
  clearSelection?: () => void;
  minRangeDays?: number;
  restrictToSameMonth?: boolean;
  popupClassName?: string;
  panelRenderWrapper?: (
    children: React.ReactNode,
    draftRange: [Dayjs | null, Dayjs | null],
  ) => React.ReactNode;
}

const TimeframePicker: React.FC<TimeframePickerProps> = ({
  selectedRange,
  onRangeChange,
  clearSelection,
  minRangeDays = MIN_RANGE_DAYS,
  restrictToSameMonth = false,
  popupClassName,
  panelRenderWrapper,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] =
    useState<[Dayjs | null, Dayjs | null]>(selectedRange);
  const [activePresetKey, setActivePresetKey] = useState<string | null>(null);
  const justCompletedSelectionRef = useRef(false);

  const presets = useMemo(() => createRangePresets(), []);

  useEffect(() => {
    setDraftRange(selectedRange);
  }, [selectedRange]);

  useEffect(() => {
    if (draftRange[0] && draftRange[1]) {
      const matchedPreset = presets.filter(
        (preset) =>
          preset.value[0].isSame(draftRange[0], 'day') &&
          preset.value[1].isSame(draftRange[1], 'day'),
      );
      if (matchedPreset.length === 0) {
        setActivePresetKey(null);
        return;
      }
      if (
        activePresetKey &&
        matchedPreset.some((p) => p.key === activePresetKey)
      ) {
        return;
      }
      setActivePresetKey(matchedPreset[0].key);
    } else {
      setActivePresetKey(null);
    }
  }, [draftRange, presets, activePresetKey]);

  const handleChange = useCallback(
    (dates: null | (Dayjs | null)[]) => {
      if (dates) {
        const range: [Dayjs | null, Dayjs | null] = [dates[0], dates[1]];
        setDraftRange(range);
      } else if (clearSelection) {
        clearSelection();
        setOpen(false);
      } else {
        setDraftRange([null, null]);
      }
    },
    [clearSelection],
  );

  const handleCalendarChange = useCallback((dates: (Dayjs | null)[]) => {
    if (!dates) return;
    const range: [Dayjs | null, Dayjs | null] = [dates[0], dates[1]];
    setDraftRange(range);
    if (range[0] && range[1]) {
      justCompletedSelectionRef.current = true;
    }
  }, []);

  const handlePresetClick = useCallback((preset: ITimeframePreset) => {
    setDraftRange(preset.value);
    setActivePresetKey(preset.key);
  }, []);

  const handleApply = useCallback(() => {
    if (draftRange[0] && draftRange[1]) {
      onRangeChange(
        draftRange,
        [
          draftRange[0].format(DateFormats['MM/DD/YYYY']),
          draftRange[1].format(DateFormats['MM/DD/YYYY']),
        ],
        activePresetKey,
      );
    } else {
      onRangeChange([null, null], ['', ''], null);
    }
    setOpen(false);
  }, [draftRange, onRangeChange, activePresetKey]);

  const isValidRange = useMemo(() => {
    if (!draftRange[0] || !draftRange[1]) {
      return false;
    }

    const daysDiff = draftRange[1].diff(draftRange[0], 'day') + 1;
    const maxRangeDate = draftRange[0]
      .add(MAX_RANGE_MONTHS, 'month')
      .subtract(1, 'day');
    const isWithinMaxRange = !draftRange[1].isAfter(maxRangeDate, 'day');

    if (activePresetKey === PresetKeyEnum.CurrentMonth) {
      return isWithinMaxRange;
    }

    return daysDiff >= minRangeDays && isWithinMaxRange;
  }, [draftRange, activePresetKey, minRangeDays]);

  const handleCancel = useCallback(() => {
    if (selectedRange) {
      setDraftRange(selectedRange);
    }
    setActivePresetKey(null);
    setOpen(false);
  }, [selectedRange]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setOpen(true);
        return;
      }

      if (justCompletedSelectionRef.current) {
        justCompletedSelectionRef.current = false;
        setOpen(true);
        return;
      }

      handleCancel();
    },
    [handleCancel],
  );

  const getPresetLabel = () => {
    const preset = presets.find((p) => p.key === activePresetKey);
    return preset?.label || '';
  };

  const disabledDate = useCallback(
    (current: Dayjs) => {
      if (!current) return false;

      const now = dayjs();
      const isInFuture = current.isAfter(now, 'day');

      if (restrictToSameMonth && draftRange[0] !== null) {
        const isNotSameMonth = current.month() !== draftRange[0].month();
        const isNotSameYear = current.year() !== draftRange[0].year();
        return isInFuture || isNotSameMonth || isNotSameYear;
      }

      return isInFuture;
    },
    [draftRange, restrictToSameMonth],
  );

  const showOverlay = !open && !!activePresetKey;

  return (
    <div className={styles['picker-wrapper']}>
      {showOverlay && (
        <div
          onClick={() => setOpen(true)}
          className={styles['overlay-wrapper']}
        >
          <DatePicker
            value={draftRange[0] || dayjs()}
            open={false}
            placeholder={getPresetLabel()}
            format={() => getPresetLabel()}
            allowClear={false}
            inputReadOnly
            variant="filled"
            suffixIcon={<CalendarOutlined />}
            className={styles['overlay-picker']}
          />
        </div>
      )}
      <RangePicker
        className={classNames({
          [styles['hidden-picker']]: showOverlay,
        })}
        popupClassName={popupClassName}
        value={draftRange}
        onChange={handleChange}
        onCalendarChange={handleCalendarChange}
        onOpenChange={handleOpenChange}
        disabledDate={disabledDate}
        open={open}
        onClick={() => setOpen(true)}
        variant="filled"
        allowClear
        inputReadOnly
        format={DateFormats['MM/DD/YYYY']}
        placeholder={[
          t('homeDashboard.startDate', 'Start Date'),
          t('homeDashboard.endDate', 'End Date'),
        ]}
        showTime={false}
        nextIcon={<RightOutlined />}
        superNextIcon={<DoubleRightOutlined />}
        prevIcon={<LeftOutlined />}
        superPrevIcon={<DoubleLeftOutlined />}
        suffixIcon={<CalendarOutlined />}
        panelRender={(panelNode) => (
          <Flex>
            <Flex
              vertical
              align="start"
              gap={8}
              className={styles['preset-sidebar']}
            >
              {presets.map((preset) => (
                <Button
                  key={preset.key}
                  className={classNames(styles['preset-item'], {
                    [styles['active-preset-item']]:
                      activePresetKey === preset.key,
                  })}
                  onClick={() => handlePresetClick(preset)}
                  type="text"
                >
                  {preset.label}
                </Button>
              ))}
            </Flex>
            {panelRenderWrapper
              ? panelRenderWrapper(panelNode, draftRange)
              : panelNode}
          </Flex>
        )}
        renderExtraFooter={() => (
          <Flex
            justify="end"
            gap={8}
            className={styles['extra-footer-wrapper']}
          >
            <BaseButton
              label={t('controls.cancel', 'Cancel')}
              type="secondary"
              className={styles['secondary-button']}
              onClick={handleCancel}
            />
            <BaseButton
              label={t('controls.apply', 'Apply')}
              type="primary"
              className={styles['primary-button']}
              onClick={handleApply}
              disabled={!isValidRange}
            />
          </Flex>
        )}
      />
    </div>
  );
};

export default TimeframePicker;
